// 판매 관리 모듈 타입 정의

export interface Customer {
  id: string;
  code: string;
  name: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  territory?: string;
  customerGroup: string;
  creditLimit: number;
  outstandingAmount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerGroup {
  id: string;
  name: string;
  customerCount: number;
}

export interface Territory {
  id: string;
  name: string;
  customerCount: number;
}

// 견적서
export interface Quotation {
  id: string;
  quotationNo: string;
  customerId: string;
  customerName: string;
  items: QuotationItem[];
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  validUntil: string;
  status: QuotationStatus;
  notes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface QuotationItem {
  itemId: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  amount: number;
}

export type QuotationStatus = 'draft' | 'submitted' | 'open' | 'ordered' | 'lost' | 'cancelled';

export const QUOTATION_STATUS_LABELS: Record<QuotationStatus, string> = {
  draft: '초안',
  submitted: '제출됨',
  open: '진행중',
  ordered: '주문전환',
  lost: '실패',
  cancelled: '취소',
};

export const QUOTATION_STATUS_COLORS: Record<QuotationStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
  submitted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  open: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  ordered: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  lost: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
};

// 판매 주문
export interface SalesOrder {
  id: string;
  orderNo: string;
  customerId: string;
  customerName: string;
  quotationId?: string;
  items: SalesOrderItem[];
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  deliveryDate?: string;
  status: SalesOrderStatus;
  paymentStatus: PaymentStatus;
  deliveryStatus: DeliveryStatus;
  notes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SalesOrderItem {
  itemId: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  deliveredQty: number;
  unitPrice: number;
  discount: number;
  amount: number;
}

export type SalesOrderStatus = 'draft' | 'submitted' | 'to_deliver' | 'to_bill' | 'completed' | 'cancelled';

export const SALES_ORDER_STATUS_LABELS: Record<SalesOrderStatus, string> = {
  draft: '초안',
  submitted: '제출됨',
  to_deliver: '배송대기',
  to_bill: '청구대기',
  completed: '완료',
  cancelled: '취소',
};

export const SALES_ORDER_STATUS_COLORS: Record<SalesOrderStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
  submitted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  to_deliver: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  to_bill: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export type PaymentStatus = 'unpaid' | 'partial' | 'paid' | 'overdue';

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  unpaid: '미결제',
  partial: '부분결제',
  paid: '결제완료',
  overdue: '연체',
};

export type DeliveryStatus = 'not_delivered' | 'partial' | 'delivered';

export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  not_delivered: '미배송',
  partial: '부분배송',
  delivered: '배송완료',
};

// 판매 청구서
export interface SalesInvoice {
  id: string;
  invoiceNo: string;
  customerId: string;
  customerName: string;
  salesOrderId?: string;
  items: SalesInvoiceItem[];
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  paidAmount: number;
  outstandingAmount: number;
  dueDate: string;
  status: InvoiceStatus;
  notes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SalesInvoiceItem {
  itemId: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  amount: number;
}

export type InvoiceStatus = 'draft' | 'submitted' | 'unpaid' | 'partial' | 'paid' | 'overdue' | 'cancelled';

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: '초안',
  submitted: '제출됨',
  unpaid: '미결제',
  partial: '부분결제',
  paid: '결제완료',
  overdue: '연체',
  cancelled: '취소',
};

export const INVOICE_STATUS_COLORS: Record<InvoiceStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
  submitted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  unpaid: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  partial: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
};

// 헬퍼 함수
export function generateQuotationNo(): string {
  const date = new Date();
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `QTN-${dateStr}-${random}`;
}

export function generateSalesOrderNo(): string {
  const date = new Date();
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `SO-${dateStr}-${random}`;
}

export function generateInvoiceNo(): string {
  const date = new Date();
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `INV-${dateStr}-${random}`;
}

// 판매 통계
export interface SalesSummary {
  totalSales: number;
  totalOrders: number;
  avgOrderValue: number;
  pendingOrders: number;
  overdueInvoices: number;
  outstandingAmount: number;
}
